/**
 * Contrato de servicio de los colectores. API de webadmin2 (lambda `contracts`)
 * → JWT post-MFA (`webadmin2.jwt`), igual que splits.ts.
 *
 * El contrato vive en `collection_config` (`PRODUCT#<id>` / `contract_html`). Lo
 * crea el onboarding del portal vendor; los colectores dados de alta desde webadmin
 * quedan sin él, y esta API permite generarlo. La descarga del PDF sigue yendo por
 * CoreDB (`downloadContractPdf` de collectors.ts).
 */
const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ?? "http://localhost:3000";

export type TaxCategory = "ph" | "pj";

export interface ContractData {
  tax_category:     TaxCategory | "";
  name:             string;   // pj: razón social · ph: nombre
  surname:          string;   // solo ph
  tax_id:           string;
  dba:              string;   // nombre de fantasía
  addr_street:      string;
  addr_number:      string;
  addr_floor:       string;
  addr_office:      string;
  addr_postal_code: string;
  addr_city:        string;
  addr_state:       string;
  rep_name:         string;   // apoderado (solo pj)
  rep_surname:      string;
  rep_id:           string;
  rep_title:        string;
}

export interface ContractStatus {
  collector:          string;
  has_contract:       boolean;
  contract_version:   string | null;
  generated_by:       string | null;   // solo si lo generó webadmin
  generated_at:       string | null;
  payments_processor: string;
  can_generate:       boolean;
  prefill:            ContractData;
}

function authHeader(): Record<string, string> {
  const jwt = localStorage.getItem("webadmin2.jwt");
  return jwt ? { Authorization: `Bearer ${jwt}` } : {};
}

async function authFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const res = await fetch(url, { ...init, headers: { ...authHeader(), ...(init.headers ?? {}) } });
  if (res.status === 401) {
    localStorage.removeItem("webadmin2.jwt");
    localStorage.removeItem("webadmin2.email");
    if (typeof window !== "undefined") window.location.replace("/login");
  }
  return res;
}

async function json<T>(res: Response): Promise<T> {
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error ?? `HTTP ${res.status}`);
  return body as T;
}

/** Ids de los colectores que tienen contrato (cacheado 5 min en la lambda). */
export async function listCollectorsWithContract(refresh = false): Promise<Set<string>> {
  const res = await authFetch(`${API_BASE}/collectors/contracts${refresh ? "?refresh=1" : ""}`);
  const b = await json<{ with_contract: string[] }>(res);
  return new Set(b.with_contract);
}

export async function getContractStatus(collectorId: string): Promise<ContractStatus> {
  return json(await authFetch(`${API_BASE}/collectors/${encodeURIComponent(collectorId)}/contract`));
}

/** Arma el contrato con los datos SIN guardarlo; devuelve el HTML para revisarlo. */
export async function previewContract(collectorId: string, data: ContractData): Promise<string> {
  const res = await authFetch(`${API_BASE}/collectors/${encodeURIComponent(collectorId)}/contract`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...data, preview: true }),
  });
  return (await json<{ html: string }>(res)).html;
}

/** Genera y guarda el contrato. Falla con 409 si el colector ya tiene uno. */
export async function createContract(collectorId: string, data: ContractData): Promise<void> {
  await json(await authFetch(`${API_BASE}/collectors/${encodeURIComponent(collectorId)}/contract`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data),
  }));
}
